import { BubbleMenu as BaseBubbleMenu, useEditorState } from "@tiptap/react";
import deepEql from "fast-deep-equal";
import React, { useCallback, useRef } from "react";

import { MenuProps, ShouldShowProps } from "../../../../components/menus/types";

import { Icon, PopoverMenu, SidebarMenu, Toggle } from "utils/ui";
import type { ColorPickerButtonHandle } from "utils/ui/ColorPickerButton";
import { getCellsForSelection, getSelectedRowIndex } from "../../utils";
import { isRowGripSelected } from "./utils";

export const TableRowMenu = React.memo(({ editor }: MenuProps): JSX.Element => {
  const colorPickerRef = useRef<ColorPickerButtonHandle>(null);
  const shouldShow = useCallback(
    ({ view, state, from }: ShouldShowProps) => {
      if (!state || !from) {
        return false;
      }

      return isRowGripSelected({ editor, view, state, from });
    },
    [editor]
  );

  const onToggleHeaderRow = useCallback(() => {
    editor.chain().focus().toggleHeaderRowShading().run();
  }, [editor]);

  const onAddRowBefore = useCallback(() => {
    editor.chain().focus().addRowBefore().run();
  }, [editor]);

  const onAddRowAfter = useCallback(() => {
    editor.chain().focus().addRowAfter().run();
  }, [editor]);

  const onDeleteRow = useCallback(() => {
    editor.chain().focus().deleteRow().run();
  }, [editor]);

  const onChangeColor = useCallback(
    (color: string) => {
      editor.chain().focus().setCellsBackgroundColor(color).run();
    },
    [editor]
  );

  const onClearColor = useCallback(() => {
    editor.chain().focus().setCellsBackgroundColor(null).run();
  }, [editor]);

  // editor.state.selection is snapshotted, not reactive on its own — useBlockEditor sets
  // shouldRerenderOnTransaction:false for perf, so this component won't re-render on
  // selection changes unless it independently subscribes via useEditorState (same pattern
  // as useTextmenuStates), otherwise the toggle/swatch below go stale after the mount.
  const { currentColor, isHeaderRow, isFirstRow } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const cells = getCellsForSelection(editor.state.selection);
      return {
        currentColor: cells?.[0]?.node?.attrs?.backgroundColor ?? undefined,
        isHeaderRow: !!cells?.length && cells.every((cell) => cell.node?.type.name === "tableHeader"),
        // Only the first row can be a header — pdf-report-core only repeats a leading run of
        // header rows across page breaks, so the toggle is meaningless (and misleading) elsewhere.
        isFirstRow: getSelectedRowIndex(editor.state.selection) === 0,
      };
    },
    equalityFn: deepEql,
  });

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey="tableRowMenu"
      updateDelay={0}
      tippyOptions={{
        // Appended to #root (React's actual event-delegation root), not document.body
        // and not the scrolling editor canvas. document.body is body — #root's parent,
        // not a descendant — so content moved there via tippy's raw appendChild sits
        // outside React's delegated listeners and onClick stops firing entirely.
        // #root has no overflow of its own, so it still escapes the canvas's
        // overflow-auto clipping that originally hid this menu behind the sidebar.
        appendTo: () => document.getElementById("root") ?? document.body,
        placement: "left",
        offset: [0, 15],
        zIndex: 1000,
        popperOptions: {
          modifiers: [{ name: "flip", enabled: false }],
        },
      }}
      shouldShow={shouldShow}
    >
      <div className="w-56 p-1 rounded-lg bg-white dark:bg-black shadow-sm border border-neutral-200 dark:border-neutral-800 text-black dark:text-white">
        {isFirstRow && (
          <>
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="flex items-center gap-2 text-sm font-medium text-neutral-500">
                {/* Empty icon-width gutter so this label lines up with the icon rows below. */}
                <span className="w-4 h-4 flex-shrink-0" />
                Header row
              </span>
              <Toggle size="small" active={isHeaderRow} onChange={onToggleHeaderRow} />
            </div>

            <PopoverMenu.Divider />
          </>
        )}

        <div
          className="flex items-center justify-between px-2 py-1.5 cursor-pointer rounded hover:bg-neutral-100 dark:hover:bg-neutral-900"
          onClick={(e) => colorPickerRef.current?.toggle(e)}
        >
          <span className="flex items-center gap-2 text-sm font-medium text-neutral-500">
            <Icon name="PaintBucket" />
            Background colour
          </span>
          <SidebarMenu.ColorPicker
            ref={colorPickerRef}
            currentColor={currentColor}
            defaultDisplayColor="#ffffff"
            showClearSwatch
            onChangeColor={onChangeColor}
            onClearColor={onClearColor}
            className="h-6 w-6 min-w-6 p-0 flex items-center justify-center hover:opacity-90"
          />
        </div>

        <PopoverMenu.Divider />

        <PopoverMenu.Item
          iconComponent={<Icon name="ArrowUpToLine" />}
          close={false}
          label="Add row above"
          onClick={onAddRowBefore}
        />
        <PopoverMenu.Item
          iconComponent={<Icon name="ArrowDownToLine" />}
          close={false}
          label="Add row below"
          onClick={onAddRowAfter}
        />

        <PopoverMenu.Divider />

        <PopoverMenu.Item icon="Trash" close={false} label="Delete row" onClick={onDeleteRow} />
      </div>
    </BaseBubbleMenu>
  );
});

TableRowMenu.displayName = "TableRowMenu";

export default TableRowMenu;
